// ./src/components/features/Forms/FormFieldsEditModal/UsernameFieldOptionsEditor.tsx
import React from "react";
import { Stack, Switch, Select, Text, NumberInput, Group } from "@mantine/core";
import { UsernameFieldOptions } from "./fieldOptionsTypes";
import { LookupDictionarySelector } from "../../LookupDictionaries/LookupDictionarySelector";

export interface UsernameFieldOptionsEditorProps {
  value: UsernameFieldOptions;
  onChange: (options: UsernameFieldOptions) => void;
  minValue: number | null;
  maxValue: number | null;
  onMinValueChange: (value: number | null) => void;
  onMaxValueChange: (value: number | null) => void;
  maxValueError?: string;
}

const ALLOWED_CHARACTERS_OPTIONS = [
  { value: "alphanumeric", label: "Letters and numbers only" },
  { value: "alphanumeric_underscore", label: "Letters, numbers, underscores" },
  { value: "alphanumeric_hyphen", label: "Letters, numbers, hyphens" },
  {
    value: "alphanumeric_both",
    label: "Letters, numbers, underscores & hyphens (default)",
  },
];

export const UsernameFieldOptionsEditor: React.FC<
  UsernameFieldOptionsEditorProps
> = ({
  value,
  onChange,
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  maxValueError,
}) => {
  const handleChange = (field: keyof UsernameFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  // Generate example based on current settings
  const getExampleUsername = () => {
    const chars = value.allowedCharacters ?? "alphanumeric_both";
    let example = "john";
    if (chars.includes("underscore")) example += "_doe";
    else if (chars.includes("hyphen")) example += "-doe";
    else example += "doe";
    example += "123";
    return value.forceLowercase ? example.toLowerCase() : example;
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure username format and validation rules.
      </Text>

      {/* Length Requirements */}
      <Group grow>
        <NumberInput
          label="Min Length"
          placeholder="3"
          value={minValue ?? ""}
          onChange={(val) => onMinValueChange(val === "" ? null : Number(val))}
          min={1}
          max={50}
          size="sm"
        />
        <NumberInput
          label="Max Length"
          placeholder="30"
          value={maxValue ?? ""}
          onChange={(val) => onMaxValueChange(val === "" ? null : Number(val))}
          error={maxValueError}
          min={minValue ?? 1}
          max={100}
          size="sm"
        />
      </Group>

      {/* Character Rules */}
      <Select
        label="Allowed characters"
        description="Which characters can be used in usernames"
        data={ALLOWED_CHARACTERS_OPTIONS}
        value={value.allowedCharacters ?? "alphanumeric_both"}
        onChange={(val) =>
          handleChange(
            "allowedCharacters",
            val === "alphanumeric_both" ? undefined : val
          )
        }
        size="sm"
      />

      <Switch
        label="Force lowercase"
        description="Convert all characters to lowercase"
        checked={value.forceLowercase ?? false}
        onChange={(e) =>
          handleChange("forceLowercase", e.currentTarget.checked)
        }
        size="sm"
      />

      <Switch
        label="Must start with letter"
        description="Username cannot begin with a number or symbol"
        checked={value.mustStartWithLetter ?? true}
        onChange={(e) =>
          handleChange(
            "mustStartWithLetter",
            e.currentTarget.checked ? undefined : false
          )
        }
        size="sm"
      />

      <Switch
        label="Disallow consecutive special characters"
        description="Prevent patterns like __, --, or _-"
        checked={value.disallowConsecutiveSpecial ?? true}
        onChange={(e) =>
          handleChange(
            "disallowConsecutiveSpecial",
            e.currentTarget.checked ? undefined : false
          )
        }
        size="sm"
      />

      {/* Reserved Words */}
      <LookupDictionarySelector
        label="Reserved words dictionary"
        placeholder="Select dictionary for stop words"
        value={value.stopWordsDictionaryId ?? null}
        onChange={(id) => handleChange("stopWordsDictionaryId", id)}
        localOnly
        size="sm"
      />

      <Text size="xs" c="dimmed">
        Use a lookup dictionary to block reserved usernames like "admin",
        "support", etc.
      </Text>

      {/* Example */}
      <Text size="xs" c="dimmed">
        Example valid username:{" "}
        <Text span ff="monospace" fw={500}>
          {getExampleUsername()}
        </Text>
      </Text>
    </Stack>
  );
};
